import React, { useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import httpClient from "../../components/helpers/httpClient";
import { toast } from "react-toastify";
import "../mobile.css";
import Dropdown from "../../components/Dropdown";
import DropdownMultiselect from "../../components/DropdownMultiselect";
import { useSelector } from "react-redux";

// Mobile Components
import {
  MobileField,
  MobileTagDropdown,
  MobileNoteItem,
  MobileNoteDetail,
  MobileContactItem,
  MobilePropertyItem,
  MobileRentalItem,
} from "../components";

const SECTIONS = [
  { id: "details", label: "Details" },
  { id: "properties", label: "Properties" },
  { id: "tags", label: "Tags" },
  { id: "notes", label: "Notes" },
  { id: "contacts", label: "Contacts" },
];

export default function MobileCompanyDrawer({ open, companyID, onClose }) {
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [edit, setEdit] = useState(false);
  const [formData, setFormData] = useState({});
  const [activeSection, setActiveSection] = useState("details");

  // Additional data for other sections
  const [tags, setTags] = useState([]);
  const [tagsList, setTagsList] = useState([]);
  const [notes, setNotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rentalContracts, setRentalContracts] = useState([]);
  const [propertiesTab, setPropertiesTab] = useState("properties");
  const [selectedNote, setSelectedNote] = useState(null);

  const companyFunctions = useSelector(state => state.companiesPage.companyFunctions);

  const title = useMemo(() => {
    return formData?.company_name || (companyID ? `Company #${companyID}` : "Company");
  }, [formData?.company_name, companyID]);

  const fetchCompany = async () => {
    setLoading(true);
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/companies/get?companyID=${companyID}`
      );
      const data = response?.data ?? null;
      setCompany(data);
      if (data) {
        setFormData(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/tags/get/${companyID}?type=company`
      );
      setTags(response.data || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const fetchTagsList = async () => {
    try {
      const response = await httpClient.get(import.meta.env.VITE_API_URL + "/api/tags/get");
      setTagsList(response.data || []);
    } catch (error) {
      console.error("Error fetching tags list:", error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/notes/get/${companyID}?type=company`
      );
      setNotes(response.data || []);
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/company-contacts/get/${companyID}?type=contacts`
      );
      setContacts(response.data || []);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/properties/properties-of-company?companyID=${companyID}`
      );
      setProperties(response.data || []);
    } catch (error) {
      console.error("Error fetching properties:", error);
      setProperties([]);
    }
  };

  const fetchRentalContracts = async () => {
    try {
      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/rental-contracts/of-company?companyID=${companyID}&detailed=true`
      );
      setRentalContracts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching rental contracts:", error);
      setRentalContracts([]);
    }
  };

  useEffect(() => {
    if (!open || !companyID) return;
    setActiveSection("details");
    setEdit(false);
    fetchCompany();
    fetchTags();
    fetchTagsList();
    fetchNotes();
    fetchContacts();
    fetchProperties();
    fetchRentalContracts();
  }, [open, companyID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSetState = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSetSelectedCompanyFunctions = (value, action) => {
    setFormData(prev => {
      const current = prev.company_functions || [];
      if (action === "add") {
        return { ...prev, company_functions: [...current, value] };
      } else {
        return { ...prev, company_functions: current.filter(item => item.id !== value.id) };
      }
    });
  };

  const handleSave = async () => {
    try {
      await toast.promise(
        httpClient.put(import.meta.env.VITE_API_URL + `/api/company/update?companyID=${companyID}`, formData),
        {
          pending: "Updating company...",
          success: "Company updated 👍",
          error: "Failed to update company 😢",
        }
      );
      setEdit(false);
      fetchCompany();
    } catch (error) {
      console.error("Error saving company:", error);
    }
  };

  const handleAddTag = async (tagID) => {
    try {
      await httpClient.put(
        import.meta.env.VITE_API_URL + `/api/tags/add/${companyID}?tagID=${tagID}&type=company`
      );
      fetchTags();
    } catch (error) {
      console.error("Error adding tag:", error);
    }
  };

  const handleRemoveTag = async (tagID) => {
    try {
      await httpClient.delete(
        import.meta.env.VITE_API_URL + `/api/tags/remove/${companyID}?tagID=${tagID}&type=company`
      );
      fetchTags();
    } catch (error) {
      console.error("Error removing tag:", error);
    }
  };

  const handleCreateTag = async (tagValue) => {
    try {
      const response = await httpClient.post(
        import.meta.env.VITE_API_URL + `/api/tags/create?tagValue=${tagValue}`
      );
      const newTag = response.data;
      await handleAddTag(newTag.id);
      fetchTagsList();
    } catch (error) {
      console.error("Error creating tag:", error);
    }
  };

  const renderSectionContent = () => {
    if (loading) {
      return <div style={{ color: "var(--secondairy-medium-gray)", padding: "20px", textAlign: "center" }}>Loading details...</div>;
    }
    
    if (!formData?.id && activeSection === "details") {
      return <div style={{ color: "var(--secondairy-medium-gray)", padding: "20px", textAlign: "center" }}>No company data found.</div>;
    }

    switch (activeSection) {
      case "details":
        return renderDetailsSection();
      case "properties":
        return renderPropertiesSection();
      case "tags":
        return renderTagsSection();
      case "notes":
        return renderNotesSection();
      case "contacts":
        return renderContactsSection();
      default:
        return null;
    }
  };

  const renderDetailsSection = () => (
    <>
      <MobileField 
        label="Name" 
        value={formData.company_name} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="company_name"
            value={formData.company_name || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Number" 
        value={formData.company_number} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="company_number"
            value={formData.company_number || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Type" 
        value={formData.company_type} 
        edit={edit}
        component={
          <Dropdown
            listArray={["NV", "BV", "VOF", "CV", "MS", "CommV"]}
            state={formData.company_type}
            setState={(val) => handleSetState("company_type", val)}
            width="100%"
            height="40px"
          />
        }
      />
      <MobileField 
        label="Functions" 
        value={formData.company_functions?.map(f => f.function).join(", ")} 
        edit={edit}
        component={
          <DropdownMultiselect
            listArray={companyFunctions}
            state={formData.company_functions || []}
            setState={handleSetSelectedCompanyFunctions}
            displayValueKey="function"
            width="100%"
          />
        }
      />
      <MobileField 
        label="Email" 
        value={formData.email} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="email"
            value={formData.email || ""}
            onChange={handleInputChange}
            type="email"
          />
        }
      />
      <MobileField 
        label="Website" 
        value={formData.website} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="website"
            value={formData.website || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Phone" 
        value={formData.phone} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="phone"
            value={formData.phone || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Shares" 
        value={formData.total_amount_of_shares} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="total_amount_of_shares"
            value={formData.total_amount_of_shares || ""}
            onChange={handleInputChange}
          />
        }
      />
      
      <div className="mobile-section-divider">
        <span>ADDRESS</span>
      </div>
      <MobileField 
        label="Street" 
        value={formData.street} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="street"
            value={formData.street || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Postal" 
        value={formData.postal_code} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="postal_code"
            value={formData.postal_code || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="City" 
        value={formData.city} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="city"
            value={formData.city || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Province" 
        value={formData.province} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="province"
            value={formData.province || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Country" 
        value={formData.country} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="country"
            value={formData.country || ""}
            onChange={handleInputChange}
          />
        }
      />

      <div className="mobile-section-divider">
        <span>SHIPPING ADDRESS</span>
      </div>
      <MobileField 
        label="Street" 
        value={formData.shipping_street} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="shipping_street"
            value={formData.shipping_street || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Postal" 
        value={formData.shipping_postal} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="shipping_postal"
            value={formData.shipping_postal || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="City" 
        value={formData.shipping_city} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="shipping_city"
            value={formData.shipping_city || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Province" 
        value={formData.shipping_province} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="shipping_province"
            value={formData.shipping_province || ""}
            onChange={handleInputChange}
          />
        }
      />
      <MobileField 
        label="Country" 
        value={formData.shipping_country} 
        edit={edit}
        component={
          <input 
            className="mobile-login-input" 
            style={{ color: 'black', background: 'white', padding: '8px' }}
            name="shipping_country"
            value={formData.shipping_country || ""}
            onChange={handleInputChange}
          />
        }
      />
    </>
  );

  const renderPropertiesSection = () => (
    <div className="mobile-section-content">
      <div className="mobile-tab-switcher">
        <button 
          className={`mobile-tab-btn ${propertiesTab === "properties" ? "active" : ""}`}
          onClick={() => setPropertiesTab("properties")}
        >
          Properties
        </button>
        <button 
          className={`mobile-tab-btn ${propertiesTab === "rentals" ? "active" : ""}`}
          onClick={() => setPropertiesTab("rentals")}
        >
          Rentals
        </button>
      </div>

      {propertiesTab === "properties" ? (
        properties.length === 0 ? (
          <div className="mobile-empty-state">No properties owned by this company.</div>
        ) : (
          <div className="mobile-items-list">
            {properties.map((property, index) => (
              <MobilePropertyItem key={index} property={property} onClick={() => {}} />
            ))}
          </div>
        )
      ) : (
        rentalContracts.length === 0 ? (
          <div className="mobile-empty-state">No rental contracts linked to this company.</div>
        ) : (
          <div className="mobile-items-list">
            {rentalContracts.map((contract, index) => (
              <MobileRentalItem 
                key={contract.id || index} 
                contract={contract} 
                entityID={companyID}
                entityType="company"
                onClick={() => {}} 
              />
            ))}
          </div>
        )
      )}
    </div>
  );

  const renderTagsSection = () => (
    <div className="mobile-section-content">
      <MobileTagDropdown
        tags={tags}
        tagsList={tagsList}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
        onCreateTag={handleCreateTag}
        placeholder="Search or add tags..."
      />
    </div>
  );

  const renderNotesSection = () => (
    <div className="mobile-section-content">
      {selectedNote ? (
        <MobileNoteDetail 
          note={selectedNote} 
          onBack={() => setSelectedNote(null)} 
        />
      ) : (
        <>
          {notes.length === 0 ? (
            <div className="mobile-empty-state">No notes for this company.</div>
          ) : (
            <div className="mobile-items-list">
              {notes.map((note, index) => (
                <MobileNoteItem 
                  key={index} 
                  note={note} 
                  onClick={() => setSelectedNote(note)} 
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderContactsSection = () => (
    <div className="mobile-section-content">
      {contacts.length === 0 ? (
        <div className="mobile-empty-state">No contacts linked to this company.</div>
      ) : (
        <div className="mobile-items-list">
          {contacts.map((contact, index) => (
            <MobileContactItem 
              key={index} 
              contact={contact} 
              onClick={() => {}} 
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "min(95vw, 450px)",
          backgroundColor: "var(--mobile-bg)",
        },
      }}
    >
      <div className="mobile-drawer__header">
        <div style={{ fontWeight: 700, fontSize: 18, flex: 1 }}>{title}</div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {activeSection === "details" && (
            !edit ? (
              <IconButton onClick={() => setEdit(true)} aria-label="Edit">
                <EditIcon />
              </IconButton>
            ) : (
              <IconButton onClick={handleSave} aria-label="Save" color="primary">
                <SaveIcon />
              </IconButton>
            )
          )}
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </div>
      </div>

      <div className="mobile-section-tabs">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            className={`mobile-section-tab ${activeSection === section.id ? "active" : ""}`}
            onClick={() => {
              setActiveSection(section.id);
              setSelectedNote(null);
            }}
          >
            {section.label}
          </button>
        ))}
      </div>

      <div className="mobile-drawer__content">
        {renderSectionContent()}
      </div>
    </Drawer>
  );
}
